import React, { useState, useEffect } from 'react';
import './CookieBanner.css';
import logo from '../../assets/hansa-flex-logo.svg'

// Enhanced cookie helpers
function setCookie(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
    return document.cookie.split('; ').reduce((r, v) => {
        const parts = v.split('=');
        return parts[0] === name ? decodeURIComponent(parts[1]) : r;
    }, '');
}

function deleteCookie(name, domain = null, path = '/') {
    const domainStr = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainStr}; SameSite=Lax`;
}

function removeGoogleAnalytics() {
    // Remove GA and GTM scripts
    const gaScripts = document.querySelectorAll('script[src*="googletagmanager.com"], script[src*="google-analytics.com"]');
    gaScripts.forEach(script => script.remove());

    // Clear GA and GTM global variables
    // Note: gtag might be non-configurable (defined in HTML), so we can't delete it
    // Instead, we'll set it to a no-op function
    if (window.gtag) {
        try {
            // Try to delete if possible
        delete window.gtag;
        } catch (e) {
            // If delete fails (non-configurable), set to no-op function
            window.gtag = function() {
                // No-op: do nothing
            };
        }
    }
    if (window.ga) {
        try {
        delete window.ga;
        } catch (e) {
            window.ga = undefined;
        }
    }
    if (window.google_tag_manager) {
        try {
            delete window.google_tag_manager;
        } catch (e) {
            window.google_tag_manager = undefined;
        }
    }
    if (window.dataLayer) {
        window.dataLayer = [];
    }

    // Clear GA timers and intervals
    if (window.gaTimers) {
        window.gaTimers.forEach(timer => clearTimeout(timer));
        window.gaTimers = [];
    }
}

function deleteAllGACookies() {
    // Get all existing cookies to find GA cookies dynamically
    const allCookies = document.cookie.split(';');
    const gaCookiesToDelete = [];

    // Static list of known GA and GTM cookies
    const knownGACookies = [
        '_ga', '_gid', '_gat', '_ga_G-XG9PGVV0MN', '_gat_gtag_G-XG9PGVV0MN',
        '__utma', '__utmb', '__utmc', '__utmt', '__utmz', '__utmv',
        // GTM cookies
        '_gcl_au', '_gcl_aw', '_gcl_dc', '_gcl_gb', '_gcl_gf', '_gcl_ha',
        '_gac_gb', '_dc_gtm_UA', '_gac_UA'
    ];

    // Find all GA and GTM cookies (including dynamic ones)
    allCookies.forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim();
        if (cookieName.startsWith('_ga') || cookieName.startsWith('_gid') ||
            cookieName.startsWith('_gat') || cookieName.startsWith('__utm') ||
            cookieName.startsWith('_gcl') || cookieName.startsWith('_gac') ||
            cookieName.startsWith('_dc_gtm') || 
            cookieName.includes('GA') || cookieName.includes('google') || 
            cookieName.includes('GTM')) {
            gaCookiesToDelete.push(cookieName);
        }
    });

    // Combine static and dynamic cookie lists
    const allGACookies = [...new Set([...knownGACookies, ...gaCookiesToDelete])];

    const currentDomain = window.location.hostname;
    const domains = [
        '',  // No domain (most important)
        currentDomain,
        '.' + currentDomain,
        'localhost',
        '.localhost'
    ];

    // Add parent domains
    const hostParts = currentDomain.split('.');
    if (hostParts.length > 1) {
        domains.push('.' + hostParts.slice(-2).join('.'));
    }
    if (hostParts.length > 2) {
        domains.push('.' + hostParts.slice(-3).join('.'));
    }

    const paths = ['/', '', '/path'];

    // Multiple deletion attempts for each cookie
    allGACookies.forEach(cookieName => {
        // Method 1: Simple deletion (most effective)
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
        document.cookie = `${cookieName}=; Max-Age=0; path=/`;
        document.cookie = `${cookieName}=; Max-Age=0;`;

        // Method 2: Domain and path combinations
        domains.forEach(domain => {
            paths.forEach(path => {
                const domainStr = domain ? `; domain=${domain}` : '';
                const pathStr = path ? `; path=${path}` : '';

                // Multiple deletion methods
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathStr}${domainStr}`;
                document.cookie = `${cookieName}=; Max-Age=0${pathStr}${domainStr}`;
                document.cookie = `${cookieName}=deleted; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathStr}${domainStr}`;

                // With SameSite
                document.cookie = `${cookieName}=; Max-Age=0${pathStr}${domainStr}; SameSite=Lax`;
                document.cookie = `${cookieName}=; Max-Age=0${pathStr}${domainStr}; SameSite=Strict`;
                document.cookie = `${cookieName}=; Max-Age=0${pathStr}${domainStr}; SameSite=None; Secure`;
            });
        });
    });

    // Force immediate cleanup with a slight delay to ensure processing
    setTimeout(() => {
        allGACookies.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            document.cookie = `${cookieName}=; Max-Age=0; path=/`;
        });
    }, 100);

    // Additional cleanup after 500ms
    setTimeout(() => {
        allGACookies.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        });
    }, 500);
}

function clearAllStorage() {
    // Clear localStorage
    try {
        if (typeof Storage !== 'undefined' && localStorage) {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.includes('ga') || key.includes('_ga') || key.includes('google') ||
                    key.includes('facebook') || key.includes('fb') || key.includes('marketing'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        }
    } catch (e) {
        console.warn('Could not clear localStorage:', e);
    }

    // Clear sessionStorage
    try {
        if (typeof Storage !== 'undefined' && sessionStorage) {
            const keysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && (key.includes('ga') || key.includes('_ga') || key.includes('google') ||
                    key.includes('facebook') || key.includes('fb') || key.includes('marketing'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => sessionStorage.removeItem(key));
        }
    } catch (e) {
        console.warn('Could not clear sessionStorage:', e);
    }
}

// Translations
const translations = {
    en: {
        "Consent to Cookies & Data processing": "Consent to Cookies & Data processing",
        "We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left.": "We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left.",
        "Function": "Function",
        "Measurement": "Measurement",
        "Reject all": "Reject all",
        "Accept all": "Accept all",
        "Save + Exit": "Save + Exit",
        "Privacy notice": "Privacy notice",
        "Privacy Policy": "Privacy Policy",
        
    },
    es: {
        "Consent to Cookies & Data processing": "Consentimiento a Cookies y Procesamiento de Datos",
        "We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left.": "Utilizamos cookies para mejorar tu experiencia, analizar tráfico, personalizar contenido e integrar servicios de terceros (por ejemplo, redes sociales, videos, análisis). Tus datos pueden ser compartidos con proveedores externos. Puedes gestionar o retirar tu consentimiento en cualquier momento a través de Preferencias de Cookies en la parte inferior izquierda.",
        "Function": "Función",
        "Measurement": "Medición",
        "Reject all": "Rechazar todos",
        "Accept all": "Aceptar todos",
        "Save + Exit": "Guardar y Salir",
        "Privacy notice": "Aviso de Privacidad",
        "Privacy Policy": "Política de Privacidad",
        
    },  
};

const CookieBanner = () => {
    const language = navigator.language.slice(0, 2);
    const t = translations[language] || translations.en;

    const [showBanner, setShowBanner] = useState(true); // Show by default for demo
    const [functionChecked, setFunctionChecked] = useState(true); // Always enabled
    const [measurementChecked, setMeasurementChecked] = useState(true); // Default to checked
    const [consentStatus, setConsentStatus] = useState('none');
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // Add effect to sync global variables whenever state changes
    useEffect(() => {
        // Set global variables that external scripts can check
        window.HFAnalyticsEnabled = measurementChecked;
        window.HFConsentStatus = consentStatus;

        // Dispatch custom events for external listeners
        window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
            detail: {
                function: functionChecked,
                measurement: measurementChecked,
                consent: consentStatus
            }
        }));
    }, [functionChecked, measurementChecked, consentStatus]);

    // Update showSaveButton when measurementChecked changes
    useEffect(() => {
        setShowSaveButton(!measurementChecked);
    }, [measurementChecked]);

    // Initialize state based on existing cookies
    useEffect(() => {
        const consent = getCookie('HFCookieConsent');
        const measurementConsent = getCookie('HFMeasurementConsent') === 'true';

        if (!consent) {
            setMeasurementChecked(true); // Default to checked if no consent cookie
            setShowBanner(true);
            setConsentStatus('none');
        } else {
            setConsentStatus(consent);
            setShowBanner(false); // Hide banner if consent cookie exists
            if (consent === 'custom') {
                setMeasurementChecked(measurementConsent);
            } else if (consent === 'accepted') {
                setMeasurementChecked(true);
            } else if (consent === 'rejected') {
                setMeasurementChecked(false);
            }
        }

        // Load or block analytics based on consent
        // Note: If user previously accepted, consent was already updated in HTML
        // This ensures GTM sees correct consent state even if it loads before React mounts
        if (consent === 'accepted' || (consent === 'custom' && measurementConsent)) {
            // Ensure consent is granted (might already be set in HTML, but ensure it's current)
            if (window.gtag) {
                window.gtag('consent', 'update', {
                    'analytics_storage': 'granted',
                    'ad_storage': 'denied',
                    'ad_user_data': 'denied',
                    'ad_personalization': 'denied',
                    'functionality_storage': 'granted',
                    'personalization_storage': 'denied',
                    'security_storage': 'granted'
                });
                
                // Send explicit consent event for GTM to track
                if (window.dataLayer) {
                    window.dataLayer.push({
                        'event': 'consent_granted',
                        'consent_type': 'analytics',
                        'analytics_storage': 'granted',
                        'timestamp': new Date().toISOString()
                    });
                }
            }
            loadGoogleTagManager();
        } else if (consent === 'rejected' || (consent === 'custom' && !measurementConsent)) {
            blockAnalytics();
        }
        // Note: For users with no consent yet, defaults are already set in HTML
        // Non-EEA users get 'granted' by default, EEA users get 'denied' (GDPR compliant)
    }, []);

    const loadGoogleTagManager = () => {
        // Ensure dataLayer and gtag exist (should already be set in index.html)
        window.dataLayer = window.dataLayer || [];
        if (!window.gtag) {
        function gtag() {
            window.dataLayer.push(arguments);
        }
        window.gtag = gtag;
        }

        // Update consent to granted BEFORE loading GTM
        // This ensures GTM sees consent as granted when it loads
        window.gtag('consent', 'update', {
            'analytics_storage': 'granted',
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied',
            'functionality_storage': 'granted',
            'personalization_storage': 'denied',
            'security_storage': 'granted'
        });

        // CRITICAL: Initialize GA4 directly as fallback
        // This ensures GA4 receives data even if GTM Configuration tag isn't configured
        // This is a safety net to guarantee data flows to GA4
        if (!window.GA4_INITIALIZED) {
            window.gtag('config', 'G-XG9PGVV0MN', {
                'send_page_view': false  // We'll send pageviews manually
            });
            window.GA4_INITIALIZED = true;
        }
        
        // Send explicit consent event for GTM tracking
        // This helps GTM recognize that consent has been granted
        window.dataLayer.push({
            'event': 'consent_granted',
            'consent_type': 'analytics',
            'analytics_storage': 'granted',
            'timestamp': new Date().toISOString()
        });

        // Check if GTM is already loaded
        if (window.google_tag_manager && window.google_tag_manager['GTM-K2W5PWZF']) {
            // GTM already loaded, consent already updated above
            return;
        }

        // Small delay to ensure consent update is processed before GTM loads
        setTimeout(() => {
            // Load GTM
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-K2W5PWZF');
            
            // After GTM loads, trigger initial pageview for current page
            // Use a polling approach since script.onload doesn't always work reliably
            const checkGTMLoaded = setInterval(() => {
                if (window.google_tag_manager && window.google_tag_manager['GTM-K2W5PWZF']) {
                    clearInterval(checkGTMLoaded);
                    // Trigger pageview for initial page load
                    // Use multiple methods to ensure GA4 receives the pageview
                    if (window.dataLayer) {
                        const pagePath = window.location.pathname + window.location.search;
                        const pageTitle = document.title || 'HANSA-FLEX Spain';
                        
                        // Method 1: Standard page_view event (for GTM triggers)
                        const pageviewEvent = {
                            'event': 'page_view',
                            'page_path': pagePath,
                            'page_title': pageTitle
                        };
                        window.dataLayer.push(pageviewEvent);
                        
                        // Method 2: Initialize GA4 directly and send pageview
                        // This ensures GA4 receives data even if GTM Configuration tag isn't set up
                        if (window.gtag) {
                            // Initialize GA4 with Measurement ID (only once)
                            if (!window.GA4_INITIALIZED) {
                                window.gtag('config', 'G-XG9PGVV0MN', {
                                    'page_path': pagePath,
                                    'page_title': pageTitle
                                });
                                window.GA4_INITIALIZED = true;
                            } else {
                                // Send pageview event to GA4
                                window.gtag('event', 'page_view', {
                                    'page_path': pagePath,
                                    'page_title': pageTitle
                                });
                            }
                        }
                        
                        // Method 3: Force GA4 Configuration tag to fire by pushing to dataLayer
                        // This works even if "All Pages" trigger doesn't fire due to delayed GTM load
                        window.dataLayer.push({
                            'event': 'gtm.load'
                        });
                    }
                }
            }, 100);
            
            // Stop polling after 10 seconds (increased from 5 to allow more time for GTM to load)
            setTimeout(() => {
                clearInterval(checkGTMLoaded);
                
                // If GTM didn't load in time, still try to send pageview
                // This handles edge cases where GTM loads very slowly
                if (window.dataLayer && !(window.google_tag_manager && window.google_tag_manager['GTM-K2W5PWZF'])) {
                    const pagePath = window.location.pathname + window.location.search;
                    const pageTitle = document.title || 'HANSA-FLEX Spain';
                    window.dataLayer.push({
                        'event': 'page_view',
                        'page_path': pagePath,
                        'page_title': pageTitle
            });
                }
            }, 10000);
        }, 100);
    };

    const blockAnalytics = () => {
        // Update consent to denied FIRST (Consent Mode v2)
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'denied',
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied',
                'functionality_storage': 'denied',
                'personalization_storage': 'denied'
            });
        }

        // Remove GA/GTM and clean up immediately
        removeGoogleAnalytics();

        // Multiple cleanup attempts with delays to ensure all cookies are caught
        deleteAllGACookies();
        setTimeout(() => deleteAllGACookies(), 50);
        setTimeout(() => deleteAllGACookies(), 150);
        setTimeout(() => deleteAllGACookies(), 300);
        setTimeout(() => deleteAllGACookies(), 600);

        clearAllStorage();
    };

    const handleAcceptAll = () => {
        const newConsent = 'accepted';
        setCookie('HFCookieConsent', newConsent);
        setCookie('HFMeasurementConsent', 'true');

        // Update state immediately and synchronously
        setMeasurementChecked(true);
        setConsentStatus(newConsent);
        setShowBanner(false);

        loadGoogleTagManager();

        // Force immediate variable updates
        setTimeout(() => {
            setMeasurementChecked(true);
            window.HFAnalyticsEnabled = true;
        }, 0);
    };

    const handleRejectAll = () => {
        const newConsent = 'rejected';
        setCookie('HFCookieConsent', newConsent);
        setCookie('HFMeasurementConsent', 'false');

        // Update state immediately and synchronously
        setMeasurementChecked(false);
        setConsentStatus(newConsent);
        setShowBanner(false);

        // Block and clean up immediately
        blockAnalytics();

        // Force immediate variable updates
        setTimeout(() => {
            setMeasurementChecked(false);
            window.HFAnalyticsEnabled = false;
        }, 0);
    };

    const handleSaveExit = () => {
        const newConsent = 'custom';
        setCookie('HFCookieConsent', newConsent);
        setCookie('HFMeasurementConsent', measurementChecked.toString());

        // Update state immediately and force re-render
        setConsentStatus(newConsent);
        setShowBanner(false);

        // Handle analytics consent changes
        if (measurementChecked) {
            loadGoogleTagManager();
        } else {
            // If analytics was previously enabled but now disabled, clean up immediately
            blockAnalytics();
        }

        // Force immediate state sync by triggering a re-render
        setTimeout(() => {
            setMeasurementChecked(measurementChecked);
        }, 0);
    };

    const handleCustomizeChoice = () => {
        // No preferences modal to customize
    };

    if (!showBanner) return null;

    return (
        <div className="cookie-banner-overlay">
            {showBanner && (
                <div className="cookie-banner-modal">
                    <div className="cookie-banner-content">
                        <div className="cookie-banner-header">
                        </div>
                        <div className="cookie-banner-logo-container">
                            <img src={logo} alt="" className='cookie-banner-logo' />
                        </div>
                        <h2 className="cookie-banner-title">
                            {t["Consent to Cookies & Data processing"]}
                        </h2>

                        <p className="cookie-banner-text">
                            {t["We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left."]}
                        </p>

                        {/* Cookie categories */}
                        <div className="cookie-categories">
                            <div className="cookie-category-item">
                                <input
                                    type="checkbox"
                                    // The label sits in a sibling <span>, which
                                    // gives the checkbox no accessible name.
                                    aria-label={t["Function"]}
                                    checked={functionChecked}
                                    disabled
                                    readOnly
                                />
                                <div className="cookie-category-icon">⚙️</div>
                                <span className="cookie-category-label disabled">
                                    {t["Function"]}
                                </span>
                            </div>

                            <div className="cookie-category-item">
                                <input
                                    type="checkbox"
                                    // The label sits in a sibling <span>, which
                                    // gives the checkbox no accessible name.
                                    aria-label={t["Measurement"]}
                                    checked={measurementChecked}
                                    onChange={(e) => setMeasurementChecked(e.target.checked)}
                                />
                                <div className="cookie-category-icon">📊</div>
                                <span className="cookie-category-label">
                                    {t["Measurement"]}
                                </span>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="cookie-banner-btn-wrapper">
                            <div className="cookie-banner-primary-buttons">
                                <button
                                    className="cookie-banner-decline-btn"
                                    onClick={handleRejectAll}
                                >
                                    ❌ {t["Reject all"]}
                                </button>
                                <button
                                    className="cookie-banner-accept-btn"
                                    onClick={handleAcceptAll}
                                >
                                    ✅ {t["Accept all"]}
                                </button>
                            </div>
                            {showSaveButton && (
                                <div>
                                    <button
                                        className="cookie-save-preference-btn"
                                        onClick={handleSaveExit}
                                    >
                                        {t["Save + Exit"]}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Footer links */}
                        {/* <div className="cookie-banner-footer">
                            <a href="#" onClick={e => { e.preventDefault(); setShowPrivacyModal(true); }}>{t["Privacy notice"]}</a>
                        </div> */}
                    </div>
                </div>
            )}
            {showPrivacyModal && (
                <div className="privacy-modal-overlay">
                    <div className="privacy-modal-content">
                        <button onClick={() => setShowPrivacyModal(false)} style={{ fontWeight: 'bold', position: 'absolute', top: '1rem', right: '1rem', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', zIndex: 1, textAlign: 'center' }} aria-label="Close">✕</button>                        
                        <div className='pt-4 pb-4'>
                            <img src={logo} alt="" className='cookie-banner-logo' />
                        </div>
                        <h2 className='text-2xl font-bold text-center mb-2' style={{ fontWeight: 'bold', color: '#2c5aa0' }}>{t["Privacy Policy"]}</h2>
                        <div className='policy-title'>{t["1. Introduction"]}</div>
                        <div className='policy-text'>{t["We appreciate your interest in the online services of HANSA-FLEX AG (referred to as HANSA-FLEX in what follows). The protection of your personal data is very important to us, and we want you to feel comfortable and secure both when visiting our websites and during all business and communication processes with us. We therefore take the protection of your data and the applicable legal regulations very seriously."]}</div>
                        <div className='policy-text'>{t["With this data protection declaration (in what follows privacy declaration), we would like to inform you in detail about how we handle your personal data."]}</div>
                        <div className='policy-text'>{t["This data protection declaration complements our general terms and conditions of sale: https://www.hansa-flex.be/terms_and_conditions.html."]}</div>
                        <div className='policy-text'>{t["The current version of the privacy statement can be found on the HANSA-FLEX website under the menu item ‘Privacy Policy’: https://www.hansa-flex.be/data_privacyinformation.html."]}</div>
                        <div className='policy-text'>{t["HANSA-FLEX reserves the right and has the right to change the content of this privacy statement. This may happen when HANSA-FLEX makes new or changed services available, or if legal changes or changes in jurisprudence make the adaptation of this privacy statement necessary or useful."]}</div>
                        <div className='policy-text'>{t["This will only happen, when the legitimate interests of HANSA-FLEX consider and this is reasonable for the user."]}</div>

                        <div className='policy-title'>{t["2. Responsible intstance within the meaning of Privacy Statement (GDPR, cf. AVG, General Data Protection Regulation)"]}</div>

                        <div className='policy-bulletpoint'>
                            <p className='policy-text-item'>{t["HANSA-FLEX Hydraulics nv"]}</p>
                            <p className='policy-text-item'>{t["Industriepark-Drongen 12"]}</p>
                            <p className='policy-text-item'>{t["9031 GENT-DRONGEN"]}</p>
                            <p className='policy-text-item'>{t["Belgium"]}</p>
                            <p className='policy-text-item'>{t["Tel.: +32 9 282 36 15"]}</p>
                            <p className='policy-text-item'>{t["E-Mail: info@hansa-flex.be"]}</p>
                            <p className='policy-text-item'>{t["Internet: http://www.hansa-flex.be"]}</p>
                            <p className='policy-text-item'>{t["Managing Director: Tessa De Vos - BE 0454 891 101 - RPR Gent"]}</p>
                        </div>

                        <div className='policy-bulletpoint'>
                            <p className='policy-text-item'>{t["HANSA-FLEX AG"]}</p>
                            <p className='policy-text-item'>{t["Zum Panrepel 44"]}</p>
                            <p className='policy-text-item'>{t["D-28307 Bremen"]}</p>
                            <p className='policy-text-item'>{t["Federal Republic of Germany"]}</p>

                            <p style={{ marginTop: '8px', marginBottom: '0px' }}>{t["Tel.: +49 421 48 90 70"]}</p>
                            <p className='policy-text-item'>{t["E-Mail: info@hansa-flex.com"]}</p>
                            <p className='policy-text-item'>{t["Internet: http://www.hansa-flex.de"]}</p>

                            <p style={{ marginTop: '8px', marginBottom: '0px' }}>{t["Management: Thomas Armerding (Chairman), Florian Wiedemeyer"]}</p>
                            <p className='policy-text-item'>{t["Chairman of the Supervisory Board: Tim Hollweg"]}</p>
                            <p className='policy-text-item'>{t["District Court Bremen HRB 26530 HB; company headquarters in Bremen"]}</p>
                            <p className='policy-text-item'>{t["VAT ID: DE170352164"]}</p>
                        </div>

                        <br />
                        <div className='policy-sub-title'>{t["Data protection contact (authorised representative)"]}</div>
                        <div className='policy-text'>{t["Your trust is important to us. Therefore, we would like to speak to you reasonably at any time regarding the collection, processing and use of your personal data. Please contact our data protection officer for this purpose."]}
                        </div>

                        <div className='policy-sub-title'>{t["Data protection officer of HANSA-FLEX"]}</div>

                        <div className='policy-bulletpoint'>
                            <p className='policy-text-item'>{t["Tessa De Vos"]}</p>
                            <p className='policy-text-item'>{t["HANSA-FLEX Hydraulics nv"]}</p>
                            <p className='policy-text-item'>{t["Industriepark-Drongen 12"]}</p>
                            <p className='policy-text-item'>{t["B-9031 Drongen"]}</p>
                            <p className='policy-text-item'>{t["Belgium"]}</p>
                            <p style={{ marginTop: '8px', marginBottom: '0px' }}>{t["Tel.: +32 9 282 36 15"]}</p>
                            <p className='policy-text-item'>{t["E-Mail: privacy@hansaflex.com"]}</p>
                        </div>

                        <div className='policy-bulletpoint'>
                            <p className='policy-text-item'>{t["Max Danne"]}</p>
                            <p className='policy-text-item'>{t["HANSA-FLEX AG"]}</p>
                            <p className='policy-text-item'>{t["Zum Panrepel 44"]}</p>
                            <p className='policy-text-item'>{t["D-28307 Bremen"]}</p>
                            <p style={{ marginTop: '8px', marginBottom: '0px' }}>{t["Tel.: +49 421 48 90 7 221"]}</p>
                            <p className='policy-text-item'>{t["E-Mail: datenschutz@hansa-flex.com"]}</p>
                        </div>

                        <div className='policy-title'>{t["3. General principles"]}</div>
                        <div className='policy-text'>{t["We collect, process and use your personal data in compliance with the applicable legal regulations on data protection of the Federal Republic of Germany, in particular the Telemediengesetz (TMG, German Telemedia Act), the Rundfunkstaatsvertrag (RStV, German National Broadcasting Treaty), Privacy Statement (GDPR, German AVG, General Data Protection Regulation), the Telekommunikationsgesetz (TKG, German Telecommunications Act) and the like, as well as the data protection regulations of the European Union."]}
                        </div>

                        <div className='policy-text'>{t["We collect and process personal data of our users only to the extent required for the provision of a functioning website and our content and services (Art. 6 para. 1 par. f GDPR). You will get an explanation of this collection under ‘Collection of data when visiting the website’."]}
                        </div>

                        <div className='policy-text'>{t["Further personal data will only be collected, processed and used by us with your own consent, if you provide it voluntarily, for example within the scope of an enquiry, search, application or order via our e-mail address, our contact form or via our websites, when registering for the services offered by HANSA-FLEX with mandatory registration subscription to our newsletter or for the motivation, content elaboration or amendment and settlement of a contract on the use of the services offered by HANSA-FLEX with mandatory registration and the purchase of the goods and services offered by HANSA-FLEX and provided for settlement purposes (art. 6 para. 1 par. a GDPR)."]}
                        </div>

                        <div className='policy-text'>{t["Your personal data cannot be accessed by other users of HANSA-FLEX's online offers."]}
                        </div>

                        <div className='policy-sub-title'>{t["Legal basis for data processing:"]}</div>
                        <div className='policy-text'> {t["Insofar as we obtain consent from the person concerned for the processing of personal data, Art. 6 para. 1 par. a EU Data Protection Regulation (GDPR) serves as the legal basis."]}
                        </div>

                        <div className='policy-text'>{t["When processing personal data required for the fulfilment of a contract where the contracting party is the person concerned, Art. 6(1) par. b GDPR serves as the legal basis. This also applies to processing required for measures prior to a contract."]}
                        </div>

                        <div className='policy-text'>{t["Insofar as a processing of personal data is required for the fulfilment of a legal obligation to be fulfilled by our company, Art. 6 para. 1 par. c GDPR serves as the legal basis."]}
                        </div>

                        <div className='policy-text'>{t["In the event that matters of vital interest of the person concerned or another natural person require processing of personal data, Art. 6 para. 1 par. d GDPR serves as the legal basis."]}
                        </div>

                        <div className='policy-text'>{t["If the processing is required because of a legitimate interest of our company or a third party and the interests, fundamental rights and freedoms of the person concerned do not consider the first-mentioned interest, art. 6 para. 1 par. f GDPR serves as the legal basis for the processing."]}
                        </div>

                        <div className='policy-sub-title'>{t["Legitimate interests may include, in particular:"]}
                        </div>
                        <div className='policy-bulletpoint'>
                            <div className='policy-bulletpoint-item'> {t["the answering of requests,"]} </div>
                            <div className='policy-bulletpoint-item'> {t["the implementation of direct marketing measures,"]} </div>
                            <div className='policy-bulletpoint-item'> {t["the provision of services and/or information intended for you,"]} </div>
                            <div className='policy-bulletpoint-item'> {t["the processing and transfer of personal data for internal resp. administrative purposes,"]} </div>
                            <div className='policy-bulletpoint-item'> {t["the operation and management of our website,"]} </div>
                            <div className='policy-bulletpoint-item'> {t["the technical support of users,"]} </div>
                            <div className='policy-bulletpoint-item'> {t["the avoidance and concealment of fraud and criminal offences,"]} </div>
                            <div className='policy-bulletpoint-item'> {t["ensuring the security of the network and the protection of data, insofar as these interests are always in harmony with applicable law and the rights and freedom of the user."]} </div>
                        </div>

                        <div className='policy-text'>{t["We will not forward your personal data to third parties for advertising or marketing purposes or for other, in particular commercial and/or advertising purposes, unless you have expressly given your consent to the forwarding of your data for the aforementioned purposes, or if we have reached an agreement on order processing with a service provider engaged by us, or if the transfer of data is expressly permitted or even prescribed by legal regulations."]}
                        </div>

                        <div className='policy-sub-title'>{t["Your rights as a data subject"]}</div>
                        <div className='policy-text'>{t["HANSA-FLEX would like to inform you about your rights here."]}
                        </div>

                        <div className='policy-text'>{t["You have the right,"]}</div>
                        <div className='policy-bulletpoint'>
                            <div className='policy-bulletpoint-item'> {t["to request confirmation from us as to whether your relevant personal data are being processed; if this is the case, you have a right to information about this personal data and to the information listed in detail in Art. 15 GDPR."]} </div>
                            <div className='policy-bulletpoint-item'> {t["to require the handover of the data concerning you within the limitations of Art. 20 GDPR in a commonly used electronic, machine-readable data format. This also includes handing over (as far as possible) to another controller directly designated by you."]} </div>
                            <div className='policy-bulletpoint-item'> {t["to require us to rectify your data if it is incorrect, inapplicable and/or incomplete. Correction also includes completion by declarations or communication."]} </div>
                            <div className='policy-bulletpoint-item'> {t["to require us to delete any personal data concerning you immediately, if one of the reasons described in detail in Art. 17 GDPR applies."]} </div>
                            <div className='policy-bulletpoint-item'> {t["to revoke any consent granted by you with effect for the future, without any disadvantages arising for you."]} </div>
                            <div className='policy-bulletpoint-item'> {t["to request from us the restriction of processing, when one of the art. 18 par enumerated conditions applies."]} </div>
                            <div className='policy-bulletpoint-item'> {t["to object to the processing of personal data concerning you at any time for reasons due to a very special situation. We will then no longer process the personal data unless we can present compelling reasons for protection that outweigh your interests, rights and freedoms, or if the processing serves to do so in order to assert, exercise or defend legal claims (art. 21 GDPR)."]}</div>
                        </div>


                        <div className='policy-text'>{t["Data that is subject to a legal retention period is unfortunately not allowed to be deleted by us. If you would like us to stop recording data about you or contacting you in the future, we will gladly include you in a blocked list."]}
                        </div>

                        <div className='policy-text'>{t["If you believe that any processing of the personal data concerning you violates the GDPR, you may apply to another public authority or seek legal assistance from the following without any adverse consequences:"]}
                        </div>
                        <div className='policy-bulletpoint'>
                            <div className='policy-bulletpoint-item'> {t["by post to HANSA-FLEX (see imprint),"]}</div>
                            <div className='policy-bulletpoint-item'>{t["by e-mail to privacy@hansaflex.com or datenschutz@hansa-flex.com."]}</div>
                            <div className='policy-bulletpoint-item'>{t["to a competent supervisory authority in the Member State of your residence, your place of work or the place of suspected violation"]}</div>
                        </div>


                        <div className='policy-title'>{t["4. Collection of data when visiting the website"]}</div>

                        <div className='policy-text'>{t["In principle, You can visit our website at www.hansa-flex.be without providing any data about Your person. However, every access to our website and every call to the files stored there is logged and saved in a protocol file, the server log file. We hereby automatically collect and store in our server log files only that data which your internet browser transmits to us."]}
                        </div>

                        <div className='policy-text'>{t["These include:"]}
                        </div>
                        <div className='policy-bulletpoint'>
                            <div className='policy-bulletpoint-item'>{t["browser type/version"]}</div>
                            <div className='policy-bulletpoint-item'>{t["operating system used"]}</div>
                            <div className='policy-bulletpoint-item'>{t["referrer URL (the previously visited internet page)"]}</div>
                            <div className='policy-bulletpoint-item'>{t["host name of the computer trying to access it (IP address)"]}</div>
                            <div className='policy-bulletpoint-item'>{t["date and time of the server request"]}</div>
                            <div className='policy-bulletpoint-item'>{t["name of the called file"]}</div>
                            <div className='policy-bulletpoint-item'>{t["amount of data transferred"]}</div>
                            <div className='policy-bulletpoint-item'>{t["notification of successful access"]}</div>
                        </div>

                        <div className='policy-text'>{t["Without this data, it would not be technically possible in part to display and present the content of the web page. To this extent, the registration of the data is imperative. We cannot assign this data to specific persons. This data is not merged with other data sources. Subject to any legal retention obligations, users' IP addresses are anonymised or deleted after cessation of use. In the case of anonymisation, the IP addresses are changed so that individual data on personal or business relationships can no longer or only with a proportionally unreasonable expenditure of time, costs and labour be assigned to a specific or identifiable natural person."]}
                        </div>

                        <div className='policy-text'>{t["The temporary storage and statistical evaluation of the data that cannot be attributed to a specific person serves exclusively for system security and data protection purposes (e.g. the recognition of possibly incorrect links and errors in programmes, and attacks on the system), and to improve our offers (e.g. by determining which offers of goods and services are particularly popular)."]}
                        </div>

                        <div className='policy-text'>{t["We reserve the right to use the data from the server log files if, based on certain concrete facts, there is a justified suspicion that users are using our websites in violation of the law or the contract, or are using them for the purpose of not paying or not paying in full any agreed rates or fees, insofar as this is required for legal proceedings."]}
                        </div>

                        <div className='policy-text'>{t["Other data about your person will not be recorded unless this data is provided by you voluntarily, e.g. in the context of an enquiry, search or order via our e-mail address."]}
                        </div>

                        <div className='policy-text'>{t["Legal basis for the temporary storage of the data and log files is Art. 6 par. 1 par. f GDPR with the aforementioned justified interests."]}
                        </div>

                        <div className='policy-title'>{t["5. Services and personal data collected"]}</div>


                        <div className='policy-text'>{t["On HANSA-FLEX websites, we offer a range of services that require you to provide some personal data if you want to use them."]}
                        </div>

                        <div className='policy-bulletpoint'>
                            <div className='policy-bulletpoint-item'>{t["Registration in the HANSA-FLEX online shop https://shop.hansa-flex.be"]}</div>
                            <div className='policy-bulletpoint-item'>{t["My user account in the HANSA-FLEX online shop https://shop.hansa-flex.be"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Storage and access to the text of a contract"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Registration in the HANSA-FLEX customer portal https://my.hansa-flex.be"]}</div>
                            <div className='policy-bulletpoint-item'>{t["‘My profile’ in the HANSA-FLEX customer portal https://my.hansa-flex.be"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Use of our X-CODE database for product identification"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Declaration when using our configurator for luminaire manufacture"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Use of our call-back form"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Use of our contact form"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Use of our feedback form"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Use of our practice manual order form"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Using our address book order form"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Using our order form for our customer magazine ‘HYDRAULIKPRESSE’ (HYDRAULIC PRESS)"]}</div>
                        </div>

                        <div className='policy-text'>{t["To use these services, some personal data are collected, which you enter yourself and by which you also consent to the processing. Legal basis for this type of processing is Art. 6(1) par. a GDPR. Please also observe the section ‘Your rights as a data subject’ in this data protection statement."]}
                        </div>

                        <div className='policy-title'>{t["6. Job Applications"]}</div>

                        <div className='policy-text'>{t["You can apply to us for vacancies, training courses, practicals for pupils and students and for positions for graduates / bachelors / masters, or on your own initiative."]}
                        </div>

                        <div className='policy-text'>{t["We expressly point out that job applications, in particular curricula vitae, certificates and other data and documents transmitted to us, may contain particularly sensitive data about mental and physical health, racial or ethnic origin, about political, religious or philosophical beliefs, trade union or political party membership or about sexual orientation. Please also note that the transmission of data by e-mail is unencrypted and that the data may therefore be disclosed or falsified by unauthorised persons under certain circumstances. If you transmit such data and documents to us by e-mail when applying for a job, you expressly agree that we may collect, process and use such data and documents for the purpose of processing the application. The collection, processing and use of such data and documents will be carried out in accordance with this data protection declaration and the otherwise applicable legal regulations."]}
                        </div>

                        <div className='policy-text'>{t["In the case of applications with a concrete reference to a position (direct application), your data and documents will be made accessible to both the deregistering department and the competent personnel department. If you have applied to us without a concrete reference to a position (spontaneous application), your data and documents will be made accessible to both the central personnel department and decentralized personnel departments of our company, insofar as these vacancies match your profile. Your data and documents will not be transferred to third parties."]}
                        </div>

                        <div className='policy-text'>{t["Deletion of the transferred data and documents occurs in the event of a rejection or negative decision as a result of your application for employment at the earliest 3 months after completion of the application process. This does not apply if legal regulations do not permit deletion or if further storage is required for purposes of proof, or if you have agreed to longer storage. Thereafter, the results are stored only in anonymized form, i.e. without the mention of your name, for further statistical evaluation. No natural person can be traced from this statistical data record, and it serves only as a basis for statistical evaluations. If you agree to the registration of your application in our database under the condition that the data and documents you have transferred are also to be taken into account in the filling of other job vacancies in the future, your data and documents will be stored in the applicant database for 12 months and then either automatically deleted or only stored in anonymized form, i.e. without mentioning your name, for further statistical evaluation. If a contract is concluded after your application, your data may be stored and used for the purpose of the usual organizational and management process in compliance with the applicable legal regulations."]}
                        </div>

                        <div className='policy-text'>{t["You have the option to withdraw all or part of your application at any time. You can also request at any time that all or some of the data and files you have transferred be deleted or modified from our database of applicants. However, certain data about your application must be stored for a limited period of 3 months in order to comply with legal requirements, in particular the duty of proof from the Allgemeine Gleichbehandlungsgesetz (AGG, German General Equal Treatment Act)."]}
                        </div>

                        <div className='policy-title'>{t["7. Use and forwarding of personal data"]}</div>


                        <div className='policy-text'>{t["As a rule, we do not pass on your personal data to third parties, unless you have expressly consented to the forwarding of your data for the aforementioned purposes or the forwarding is expressly permitted or required by law."]}
                        </div>


                        <div className='policy-sub-title'>{t["Service providers involved in contract processing"]}</div>


                        <div className='policy-text'>{t["According to Article 6 Para. 1 par. b GDPR and Article 6 Para. 1 par. f GDPR, HANSA-FLEX may transfer the data collected within the scope of contract fulfillment - if required - to subsidiaries of companies, group companies and partner companies and agencies involved in contract fulfillment, as well as to other external service providers we use for contract fulfillment, provided that the purpose of use of the data remains intact. This includes in particular the forwarding companies responsible for the delivery of ordered goods and the institutions responsible for payment processing, the subsidiaries of http://www.hansa-flex.com/niederlassungen.html, companies of the http://www.hansa-flex.com/unternehmen/unternehmensgruppe.html group and partner companies and representations http://www.hansa-flex.com/niederlassungen/partner.html and other external service providers. In these cases, we forward the data in accordance with the guidelines of the applicable data protection legal regulations (in particular the Privacy Statement - GDPR, German AVG, General Data Protection Regulation). The scope of the transferred data is kept to a minimum. These companies may only use your data to complete the order and not for other purposes, and are bound by a data processing agreement in the context of an assignment under Art. 28 GDPR."]}
                        </div>

                        <div className='policy-sub-title'>{t["Use of data for purposes of our own consulting, advertising and market research"]}</div>

                        <div className='policy-text'>{t["According to Art. 6 para. 1 par. f GDPR, we are permitted to collect, process and use your personal data for the purpose of our own consulting, advertising and market research, especially, however, for demand-oriented design of the goods and services offered by us. Of course, you may object to the collection, processing and use of your personal data for advertising purposes at any time with effect for the future or revoke your consent. We will then no longer send you advertising information."]}
                        </div>

                        <div className='policy-text'>{t["Furthermore, we treat the data confidentially and in accordance with the applicable data protection regulations. We will not forward this data or the content of your messages to third parties without your consent, unless you have expressly agreed to the forwarding of your data for the aforementioned purposes or the forwarding is expressly permitted or even required by law."]}
                        </div>

                        <div className='policy-text'>{t["This ban on data transmission does not apply, however, to the extent that we are obliged by order of the competent authorities to provide third parties, in particular public authorities, with information on data in individual cases, if this is for the purpose of criminal prosecution, to ward off dangers by the police services of the Länder in order to fulfill the legal duties imposed by the internal security service of the FRG and the Länder, by the secret intelligence service or military counterintelligence or by the Federal Criminal Police within the scope of their duty to ward off the dangers of international terrorism, or in order to exercise intellectual property rights or pursuant to other applicable legal provisions. This does not require your consent."]}
                        </div>

                        <div className='policy-text'>{t["Any collection, processing or use of your data beyond what is described here is done only with your consent according to Art. 6 para. 1 par. a GDPR."]}
                        </div>

                        <div className='policy-sub-title'>{t["Export and processing of data in states outside the European Economic Area"]}</div>

                        <div className='policy-text'>{t["HANSA-FLEX is a global company and operates numerous websites worldwide. Data from websites for affiliates and group companies located in Germany and in the EU is stored exclusively on servers in Germany or the EU, including data transferred to these websites or to us. Data from websites for affiliates and group companies located in states outside the European Economic Area may also be stored on servers outside Germany and the EU, including data transferred to these websites or to us."]}
                        </div>

                        <div className='policy-text'>{t["We may also transfer personal data provided by you voluntarily, e.g. in the context of an inquiry, search, application or order via our email address etc., to subsidiaries located in countries outside the European Economic Area http://www.hansa-flex.com/niederlassungen.html, group companies http://www.hansa-flex.com/unternehmen/unternehmensgruppe.html and partner companies and representations http://www.hansa-flex.com/niederlassungen/partner. html and to other external service providers, if a business and local relationship exists with them and the transfer of the data is necessary for answering/processing an inquiry, a search, an application, registering for the services offered by HANSA-FLEX with mandatory registration, subscribing to our newsletter, or for the motivation, content development or amendment and completion of a contract for the use of the goods and services offered by HANSA-FLEX with mandatory registration and for billing purposes. In this case, your personal data may also be stored on servers outside of Germany or the EU."]}
                        </div>

                        <div className='policy-text'>{t["If the recipient of the data is based in a European Union (EU) member state or a treaty state of the European Economic Area (EEA) agreement, then under law applicable within the community it should be assumed that an adequate level of data protection is ensured at the recipient. In addition, for some non-EU/EEA countries, the EU Commission has decided that their legal order ensures an adequate level of data protection across the board, so that an adequate level of data protection may also be assumed at recipients of data located in these countries. This includes the following countries: Andorra, Argentina, Australia, Faroe Islands, Guernsey, Isle of Man, Israel, Jersey, Canada, New Zealand, Switzerland, Uruguay, USA (Safe Harbor) http://ec.europa.eu/justice/data-protection/document/international-transfers/adequacy/index_en.htm. If the recipient of data is located in a country (third state) other than the countries listed above, in order to ensure an adequate level of data protection, we have either entered into a contract with the recipient with EU standard treaty clauses, or the recipient has submitted to the Safe Harbor principles for US companies, or we have agreed with the recipient arrangements for handling personal data (Binding Corporate Rules)."]}
                        </div>

                        <div className='policy-text'>{t["The user is aware that in the countries outside the European Economic Area there may be a lower level of data protection than in the countries of the European Union."]}
                        </div>

                        <div className='policy-title'>{t["8. Cookies"]}</div>

                        <div className='policy-text'>{t["Our websites use so-called cookies in several places. A cookie (roughly equivalent to “profile file”) is a small text file, which a server stores on the hard drive of the user's computer and which the user's Internet browser saves, in order to temporarily keep available there limited information necessary for the use of the respective server. We use cookies to recognize and control processes within the HANSA-FLEX Internet offer and for the temporary storage of session data (in particular for login and to recognize your identity). This cookie is imperative for the proper functioning of the services offered by us with registration requirement of the Internet portal. You can set your Internet browser so that you are informed about the setting of cookies, decide on a case-by-case basis whether you agree to this, or basically exclude the acceptance of cookies. How to set the cookie function in your internet browser can be found in the help function of your internet browser. Accepting this cookie may be refused by you, however, inevitably leads to system errors, which cannot be remedied or corrected by us. Most of the cookies used by us are so-called “session cookies”. They are deleted automatically after the end of your visit. Our cookies do not harm your computer and do not contain viruses, Trojan horses or similar harmful programs."]}
                        </div>

                        <div className='policy-text'>{t["The legal basis for the processing of personal data with use of cookies is art. 6 paragraph 1 par. f GDPR. The legal basis for processing personal data with the use of technically necessary cookies is art. 6 par. 1 par. f GDPR. The legal basis for the processing of personal data with the use of cookies for analysis purposes is, if the user's consent is present, art. 6 paragraph 1 par. a GDPR."]}
                        </div>

                        <div className='policy-title'>{t["9. Newsletter"]}</div>
                        <div className='policy-text'>
                            {t["HANSA-FLEX Hydraulics NV also regularly informs existing customers via newsletters in a B2B context on the basis of contractual cooperation (art. 6 para. 1 par. b GDPR). With each newsletter you have the option to unsubscribe from it, in which case you will not receive any further newsletters. However, your contact data will still be stored within our ERP application, so that we can still continue to provide other services. If you do not wish this, please let us know via the contact information listed in this statement."]}
                        </div>

                        <div className='policy-title'>{t["10. Google Analytics"]}</div>
                        <div className='policy-text'>
                            {t["This website uses Google Analytics, a web analytics service provided by Google Inc. (“Google”). Google Analytics uses so-called “cookies”, text files that are stored on your computer and allow an analysis of how you use the website. The information generated by the cookie about your use of this website is generally transferred to a Google server in the USA and stored there. However, in the case of activation of IP anonymization on this website, your IP address is first abbreviated by Google within member states of the European Union or in other contracting states of the agreement of the European Economic Area. Only in exceptional cases will the full IP address be transferred to a Google server in the USA and abbreviated there. Please note that IP anonymization is active on this website. On this web page, Google Analytics was extended with the code “gat._anonymizeIp()” to ensure an anonymized recording of IP addresses (so-called IP masking). On behalf of the operator of this website, Google will use this information to evaluate your use of the website, to compile reports on website activity and to provide other services related to website usage to the website operator. The IP address transmitted by your browser within the scope of Google Analytics will not be merged with other Google data."]}
                        </div>

                        <div className='policy-text'>
                            {t["You can prevent the storage of cookies by configuring your browser software accordingly; however, we would like to point out that in this case you may not be able to use all the functions of this website to their full extent. In addition, you can prevent the registration of the data generated by the cookie and relating to your use of the website (including your IP address) to Google and the processing of this data by Google by downloading and installing the browser plug-in available under the following link: tools.google.com/dlpage/gaoptout ."]}
                        </div>

                        <div className='policy-text'>
                            {t["Further information on the terms of use and on data protection when using Google Analytics can be found at: http://www.google.com/analytics/terms/de.html or at http://www.google.com/policies/privacy/ respectively."]}
                        </div>

                        <div className='policy-title'>{t["11. Google Maps"]}</div>
                        <div className='policy-text'>
                            {t["On this website we use the offer of Google Maps. This allows us to show you interactive maps directly on the website and enables you to comfortably use the map function."]}
                        </div>

                        <div className='policy-text'>
                            {t["By visiting the website, Google receives the information that you have called up the corresponding subpage of our website. This occurs regardless of whether Google provides a user account through which you are logged in, or no user account exists. If you are logged in to Google, then your data is directly assigned to your account. If you do not want the attribution to your profile at Google, you must log out before calling up a map. Google stores your data as usage profiles and uses them for the purpose of advertising, market research and/or for demand-oriented design of its website. Such evaluation occurs in particular (even for non-logged-in users) for the provision of demand-oriented advertising and to inform other users of the social network about your activities on our website. You are entitled to a right of revocation against the creation of these user profiles, for the exercise of which you must contact Google."]}
                        </div>

                        <div className='policy-text'>
                            {t["The use of Google Maps is done in the interest of an attractive presentation of our online services and in order to be able to easily find the places mentioned by us on the website. This constitutes a legitimate interest within the meaning of Art. 6 para. 1 par. f GDPR (German AVG, General Data Protection Regulation)."]}
                        </div>

                        <div className='policy-text'>
                            {t["You will receive more information about the purpose and scope of data collection and its processing by Google Maps in the data protection statements. There you will also get more information about your rights and setting options in this regard to protect your privacy: www.google.de/intl/de/policies/privacy. Google also processes your personal data in the USA and has submitted to the EU-US Privacy Shield, www.privacyshield.gov/EU-US-Framework."]}
                        </div>

                        <div className='policy-text'>
                            {t["Google Maps is operated by Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA"]}
                        </div>

                        <div className='policy-title'>{t["12. Facebook"]}</div>
                        <div className='policy-text'>
                            {t["On this page we use so-called plug-ins from the social network Facebook. The Facebook plug-ins can be recognized by the Facebook logo or the “Like button”. When the user visits this page, the plug-in establishes a direct connection between his browser and the Facebook server. Facebook thereby receives the information that the user has visited this page with his IP address. When the user clicks the Facebook “Like button” while logged into his Facebook account, he can link the content of these pages to his Facebook profile. This allows Facebook to attribute the visit to these pages to the user's user account."]}
                        </div>

                        <div className='policy-text'>
                            {t["We point out that we as the provider of the pages are not informed about the content of the transmitted data and its use by Facebook. If the user does not wish that Facebook can assign the visit to these pages to his Facebook user account, he must first log out of his Facebook user account."]}
                        </div>

                        <div className='policy-text'>
                            {t["Data protection information from Facebook: www.facebook.com/policy.php"]}
                        </div>

                        <div className='policy-text'>
                            {t["Facebook is operated by Facebook Inc., 1601 S. California Ave, Palo Alto, CA 94304, USA"]}
                        </div>

                        <div className='policy-title'>{t["13. YouTube"]}</div>
                        <div className='policy-text'>
                            {t["We have integrated YouTube videos into our online offer, which are stored at www.youtube.com and can be played directly from our website. These are all integrated in “advanced data protection mode,” i.e. no data about you as a user is transferred to YouTube when you do not play the videos. Only when you play the videos are data transferred. We have no influence on this data transfer. This occurs regardless of whether YouTube makes a user account available through which you are logged in, or no user account exists. If you are logged in to Google, your data is directly assigned to your account."]}
                        </div>

                        <div className='policy-text'>
                            {t["You will receive more information about the purpose and scope of data collection and its processing by YouTube in the Data Protection Statement. There you will also get more information about your rights and setting options to protect your private sphere: https://policies.google.com/privacy. Google also processes your personal data in the US and has submitted to the EU-US Privacy Shield, www.privacyshield.gov/EU-US-Framework."]}
                        </div>

                        <div className='policy-text'>
                            {t["YouTube is operated by Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA"]}
                        </div>

                        <div className='policy-title'>{t["14. Storage of bank data"]}</div>
                        <div className='policy-text'>
                            {t["If we offer direct debit as a payment method and should not use a payment service provider for this purpose, the user, who chooses this payment method, is obliged to provide HANSA-FLEX with a valid direct debit authorization for his bank account. In doing so, the user must provide, among other things, the following information:"]}
                        </div>

                        <div className='policy-bulletpoint'>
                            <div className='policy-bulletpoint-item'> {t["Holder of the account"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Account number"]}</div>
                            <div className='policy-bulletpoint-item'>{t["Name of the bank"]}</div>
                            <div className='policy-bulletpoint-item'> {t["Bank code of the bank"]}</div>
                        </div>


                        <div className='policy-text'>
                            {t["Bank information can never, ever be viewed by other users."]}
                        </div>

                        <div className='policy-sub-title'>
                            {t["Internet payment systems"]}
                        </div>
                        <div className='policy-text'>
                            {t["We point out that for the payment of our Internet services, in addition to other payment methods, Internet payment systems are offered in some cases, whose operators may also be located outside the EU. These companies thereby store in particular the ordering user's payment-relevant data on servers, possibly also outside the EU. We have no influence on this."]}
                        </div>

                        <div className='policy-title'>
                            {t["15. Protection of children and young people"]}
                        </div>
                        <div className='policy-text'>
                            {t["The protection of the privacy of children and young people is very important to us. In principle, persons under 18 years of age may not transfer data to our websites without the consent of their legal representative. We will not collect, process or use information of persons known to us to be under the age of 16 without the verifiable consent of their legal representative."]}
                        </div>

                        <div className='policy-title'>
                            {t["16. Data protection"]}
                        </div>
                        <div className='policy-text'>
                            {t["Your personal data, both when registering for services offered by us with mandatory registration and when logging into the internal user service area both of the online shop at https://shop.hansa-flex.be and of the customer portal at https://my.hansa-flex.com, are encrypted by means of SSL certificate (2048 bit) transmitted over the Internet. We secure our website and other systems by technical and organizational measures against loss, destruction, access, modification or dissemination of your data by unauthorized persons. However, despite regular checks, complete protection against all dangers is not possible. Access to your account in the internal user service area both from the online shop at https://shop.hansa-flex.be and from the customer portal at https://my.hansa-flex.com is only possible after entering your personal password. You must always keep your access information confidential and close the browser window when you have finished communicating with us, especially when using the computer together with others."]}
                        </div>

                        <div className='policy-title'>
                            {t["17. External links and content."]}
                        </div>
                        <div className='policy-text'>
                            {t["The contents of our websites were compiled with the greatest care. Nevertheless, we do not guarantee topicality and completeness. We are only responsible for our own content, not for external content. For additional information, you may find links on our websites, which refer to web pages of third parties. We have checked all external links for content in violation of the law, and at that time they could not be identified. With regard to external content, there is no general duty of monitoring and control. Upon knowledge of content in violation of the law, we immediately check these links and, if necessary, remove them immediately. External links are always opened in a new browser window."]}
                        </div>

                        <div className='policy-text'>
                            {t["Status: March 2022"]}
                        </div>

                    </div>
                </div>
            )}
        </div>
    )
}

export default CookieBanner;